import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  deleteInventoryItem,
  saveInventoryCategory,
  saveInventoryItem,
  saveInventoryUnit,
} from '@/renderer/services/inventoryItemService';
import {
  SaveInventoryCategoryRequest,
  SaveInventoryItemRequest,
  SaveInventoryUnitRequest,
} from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';

export function useSaveInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveInventoryItemRequest) => {
      const result = await saveInventoryItem(payload);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to save inventory item');
      }

      return result.data!;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess(
        payload.id ? 'Inventory item updated.' : 'Inventory item created.',
      );
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useDeleteInventoryItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      companyId,
      itemId,
    }: {
      companyId: number;
      itemId: string;
    }) => {
      const result = await deleteInventoryItem(companyId, itemId);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to delete inventory item');
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess('Inventory item deleted.');
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useSaveInventoryCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveInventoryCategoryRequest) => {
      const result = await saveInventoryCategory(payload);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to save inventory category');
      }

      return result.data!;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess(
        payload.id
          ? 'Inventory category updated.'
          : 'Inventory category created.',
      );
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}

export function useSaveInventoryUnit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SaveInventoryUnitRequest) => {
      const result = await saveInventoryUnit(payload);
      if (!result.success) {
        throw new Error(result.error ?? 'Failed to save inventory unit');
      }

      return result.data!;
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all });
      apiSuccess(
        payload.id ? 'Inventory unit updated.' : 'Inventory unit created.',
      );
    },
    onError: (error: Error) => {
      apiError(error.message);
    },
  });
}
